import { createSelector } from 'reselect';
import { selectRetouchingAddableForOrder } from './retouchingGate';
import type { RetouchingRootState } from './retouchingState';

const selectCurrentOrderKey = (state: RetouchingRootState) =>
  `${state.shoot?.shoot?.event_id || state.shoot?.shoot?.id}-${
    state.additionalData?.selectedStudent?.id
  }`;

// Where a retouching product opened from the catalog should go for the order
// being built (the selected student at the current event). When retouching can
// be added to that order (selectRetouchingAddableForOrder) it goes to the photo
// picker for that product, in edit mode if the order already has retouching.
// Otherwise null, and the regular product screen stays.
export const selectRetouchingPickerPath = createSelector(
  (state: RetouchingRootState) =>
    selectRetouchingAddableForOrder(state, selectCurrentOrderKey(state)),
  selectCurrentOrderKey,
  (state: RetouchingRootState) => state.v3Order?.orders,
  (_state: RetouchingRootState, productId: number) => productId,
  (addable, orderKey, orders, productId): string | null => {
    if (!addable) return null;

    const hasRetouching = (orders?.[orderKey]?.cartItems || []).some(
      (item: { isRetouching?: boolean }) => item.isRetouching,
    );
    return hasRetouching
      ? `/v3/retouching?edit=1&orderKey=${encodeURIComponent(
          orderKey,
        )}&productId=${productId}`
      : `/v3/retouching?productId=${productId}`;
  },
);

// Where a cart retouching line's Edit link goes: the photo picker in edit mode
// for that order, prefilled with its selection. The order key travels in the
// query string because the cart shows one section per student; without it the
// picker would resolve the order from the globally selected student and edit
// the wrong one. Null when retouching cannot be changed on that order
// (selectRetouchingAddableForOrder holds the rules), so no link: following one
// would land on the legacy flat button, which addToCart's duplicate guard
// refuses, and on a multi-student cart would add to whoever is selected.
export const selectRetouchingEditPath = createSelector(
  (state: RetouchingRootState, orderKey: string | null) =>
    orderKey !== null && selectRetouchingAddableForOrder(state, orderKey),
  (_state: RetouchingRootState, orderKey: string | null) => orderKey,
  (addable, orderKey): string | null =>
    addable && orderKey !== null
      ? `/v3/retouching?edit=1&orderKey=${encodeURIComponent(orderKey)}`
      : null,
);
